import React, { useEffect, useState, memo, useRef } from "react";
import {
    TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogTitle,
    DialogActions, DialogContent, InputAdornment, Card, CardContent, Typography, IconButton, Paper,Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import DefaultActions from "../../../components/Card/DefaultActions";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
import { PaymentMethodViewModel, PaymentMethodExternalRef } from "../../../Types/ExportTypes";
import { AccountAutoComplete, PaymentTypeAutoComplete, PaymentProviderAutoComplete, PaymentProviderConfigAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import InfoButton from "../../../components/Tooltip/InfoTextField";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

const PaymentMethodEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { id } = params || {};
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { goBack, navigate } = useNavigation();

    const [viewModel, setViewModel] = useState<(PaymentMethodViewModel & { hasChanges?: boolean; originalValues?: Partial<PaymentMethodViewModel>; }) | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
    const [showExtRefs, setShowExtRef] = useState(false);
    const [editingLine, setEditingLine] = useState<PaymentMethodExternalRef | null>(null);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading]);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            authService
                .get<PaymentMethodViewModel>(`/settingsApi/GetPaymentMethod?id=${id}`)
                .then((response) => setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null))
                .catch((err: any) =>
                    showSnackbar(`${t("common.failedToFetchEntries")}: ${err.response?.data ?? err.message}`, "error")
                )
                .finally(() => setIsLoading(false));
        } else {
            const defaultModel: PaymentMethodViewModel & { originalValues: Partial<PaymentMethodViewModel>; hasChanges: boolean } = {
                createNew: true,
                global: true,
                originalValues: {},
                hasChanges: false,
            } as PaymentMethodViewModel & { originalValues: Partial<PaymentMethodViewModel>; hasChanges: boolean };
            defaultModel.originalValues = { ...defaultModel };
            setViewModel(defaultModel);
        }
    }, [id]);

    const handleChange = (field: keyof PaymentMethodViewModel, value: any) => {
        setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
    };

    const handleCancelChanges = () => {
        setViewModel((prev) => {
            if (!prev || !prev.originalValues) return prev;
            const original = prev.originalValues as PaymentMethodViewModel;
            return { ...original, originalValues: { ...original }, hasChanges: false };
        });
    };

    const validate = () => {
        if (!viewModel) return false;
        if (!viewModel.description || viewModel.description.trim() === "") {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!viewModel || !validate()) return;

        setIsLoading(true);
        try {
            const response = await authService.post<PaymentMethodViewModel>(
                "/settingsApi/CreateOrUpdatePaymentMethod",
                viewModel
            );
            setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(t("dialog.savedSuccessfully"), "success");
            if (!id) navigate({ path: "/settings/paymentmethod/edit", params: { id: response.id } });
        } catch {
            showSnackbar(t("dialog.saveErrorGeneric"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = async (newCode?: string,newDescription?: string) => {
        if (!viewModel) return;
        if (!viewModel.id) {
            showSnackbar(t("dialog.cannotCopyPreliminary"), "warning");
            return;
        }

        if (newCode && newDescription) {
            setViewModel(prev => ({ ...prev,newPaymentCode: newCode, newDescription }));
        }

        if (!viewModel?.newPaymentCode && !newCode) {
            setIsCopyModalOpen(true);
            return;
        }
        setIsCopyModalOpen(false);
        setIsLoading(true);
        try {
            const response = await authService.post<PaymentMethodViewModel>(
                "/settingsApi/CopyPaymentMethod",
                { ...viewModel, newDescription, newPaymentCode : newCode }
            );
            setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(t("dialog.copySuccess"), "success");
            navigate({ path: "/settings/paymentmethod/edit", params: { id: response.id } });
        } catch (error: any) {
            showSnackbar(`${t("dialog.copyError")}: ${error.response?.data ?? error.message}`, "error");
            setViewModel(prev => ({ ...prev, newPaymentCode: null, newDescription: null, hasChanges: true }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!viewModel || !viewModel.id) return;
        if (!window.confirm(t("dialog.deleteEntityConfirm"))) return;

        setIsLoading(true);
        try {
            await authService.post("/settingsApi/DeletePaymentMethod", viewModel);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            navigate({ path: "/settings/paymentmethod/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.deleteFailed")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectRef = (ref: PaymentMethodExternalRef) => {
        setEditingLine(ref);
    };


    const handleFieldChange = async (field: keyof PaymentMethodExternalRef, value: any) => {
        if (editingLine) {
            let updatedLine = { ...editingLine, [field]: value }
           


            setEditingLine((prev) => {
                if (!prev) return null;


                updatedLine = { ...prev, [field]: value};
                return updatedLine;
            });

        };
    }

    const validateLineEditing = (editingLine?: PaymentMethodExternalRef) => {

        if (!editingLine) return false;
        if (!editingLine.ref || editingLine.ref.trim() === "") {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return false;
        }
        return true;

    }


    const handleRemoveLine = async (editingLine?: PaymentMethodExternalRef | null) => {
        const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
        if (!isConfirmed) return;
     
        if (!editingLine) return;

        try {
            setIsLoading(true);

            setViewModel(prev => {
                const refs = prev?.externalReferences ?? [];
                const updatedRefs = refs.filter(r => {
                    if (editingLine.id != null) {
                        return r?.id !== editingLine.id;
                    }
                    if (editingLine.tempId != null) {
                        return !(r?.id == null && r?.tempId === editingLine.tempId);
                    }
                    return true; 
                });

                return {
                    ...prev,
                    externalReferences: updatedRefs
                };
            });

            setEditingLine(null);
        } catch (error: any) {
            console.error("Error removing line:", error);
            showSnackbar(`${t("dialog.lineSaveError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveLine = async () => {

        if (!editingLine) return;

        if (!editingLine.id && !editingLine.tempId) editingLine.tempId = crypto.randomUUID();
        if (!validateLineEditing(editingLine)) return;

        try {
            setIsLoading(true);

            setViewModel(prev => {
                const refs = prev?.externalReferences ?? [];
                const index = refs.findIndex(r => {
                    if (editingLine.id != null) {
                        return r?.id === editingLine.id;
                    }
                    if (editingLine.tempId != null) {
                        return r?.id == null && r?.tempId === editingLine.tempId;
                    }
                    return false;
                });

                const updatedRefs =
                    index >= 0
                        ? [...refs.slice(0, index), editingLine, ...refs.slice(index + 1)]
                        : [...refs, editingLine];

                return {
                    ...prev,
                    externalReferences: updatedRefs
                };
            });
            //showSnackbar(t("dialog.lineSaved"), "success");
            setEditingLine(null);
        } catch (error: any) {
            console.error("Error saving line:", error);
            showSnackbar(`${t("dialog.lineSaveError")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };
    type PaymentMethodCopyModalInput = {
        open: boolean;
        onClose: () => void;
        onConfirm: (newCode: string, newDescription: string) => void;
        ViewModel?: PaymentMethodViewModel | null
    };
    const PaymentMethodCopyModal = memo(({ open, onClose, onConfirm, ViewModel }: PaymentMethodCopyModalInput) => {
        const [tempCode, setTempCode] = useState("");
        const [tempDescription, setTempDescription] = useState("");
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>{t("common.enterNewCode")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("common.newCode")}
                        fullWidth
                        variant="outlined"
                        value={tempCode}
                        onChange={(e) => setTempCode(e.target.value)}
                        error={ViewModel?.paymentCode === tempCode || (!tempCode || tempCode.trim() === "")}
                        helperText={
                            ViewModel?.paymentCode === tempCode
                                ? t("common.entityCodeSame")
                                : (!tempCode?.trim())
                                    ? t("help.codeRequired")
                                    : ""
                        }
                    />
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("common.newDescription")}
                        fullWidth
                        variant="outlined"
                        value={tempDescription}
                        onChange={(e) => setTempDescription(e.target.value)}
                        error={(!tempDescription || tempDescription.trim() === "")}
                        helperText={
                            !tempDescription?.trim() ? t("help.descriptionRequired") : ""
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}> {t("common.close")}</Button>
                    <Button
                        onClick={() => {
                            if (!tempCode.trim()) {
                                showSnackbar(t("dialog.accountNoEmpty"), "warning");
                                return;
                            }

                            if (!tempDescription.trim()) {
                                showSnackbar(t("dialog.descriptionRequired"), "warning");
                                return;
                            }
                            onConfirm(tempCode?.trim(), tempDescription?.trim()); // Pass only on confirm
                        }}
                        color="primary"
                    >
                        {t("common.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    });

    return (
        <CenteredCard
            ref={toolbarRef}
            title={t("common.edit") + " " + t("common.paymentMethod")}
            isLoading={isLoading}
            handleSave={handleSave}
            handleCancel={handleCancelChanges}
            hasChanges={viewModel?.hasChanges ?? false}
        >
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
                <DefaultActions onSave={handleSave} onCopy={handleCopy} onDelete={handleDelete} />
            </Box>
            <PaymentMethodCopyModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} ViewModel={viewModel} />

            {viewModel && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.description")}
                            value={viewModel.description || ""}
                            onChange={(e) => handleChange("description", e.target.value)}
                            fullWidth
                            margin="normal"
                            error={!viewModel.description}
                            helperText={!viewModel.description ? t("help.descriptionRequired") : ""}
                        />
                    </Grid>
                    {viewModel.createNew == true && (
                    <Grid item xs={12}>
                        <FormControl fullWidth margin="normal" >
                                <PaymentTypeAutoComplete
                                key={viewModel.paymentType}
                                label={t("common.type")}
                                valueId={viewModel.paymentType}
                                onChange={(selected) => handleChange("paymentType", selected?.value || "")}
                                disabled={!viewModel.createNew}
                            />
                        </FormControl>
                    </Grid>
                    )}
                    {viewModel.createNew != true && (
                        <Grid item xs={12}>
                            <TextField
                                label={t("common.type")}
                                value={(() => {
                                    const key = `common.paymentType_${viewModel.paymentTypeDesc?.replace(/\s+/g, "_")}`;
                                    const translated = t(key, {
                                        defaultValue: viewModel.paymentTypeDesc ?? "-"
                                    });
                         
                                    return `${translated}`;
                                })()}
                                fullWidth
                                margin="normal"
                                disabled={true}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.code")}
                            value={viewModel.paymentCode || ""}
                            onChange={(e) => handleChange("paymentCode", e.target.value)}
                            fullWidth
                            margin="normal"
                        />
                    </Grid>
              
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal" >
                            <AccountAutoComplete
                                    key={viewModel.balanceAccountNo}
                                    label={t("common.balanceAccountNo")}
                                    valueId={viewModel.balanceAccountNo}
                                    onChange={(selected) => handleChange("balanceAccountNo", selected?.value || "")}
                                    infoText={t("help.balanceAccount")}
                                    error={!viewModel.balanceAccountNo?.trim() && viewModel.paymentType != "15"}
                                    helperText={(!viewModel.balanceAccountNo?.trim() && viewModel.paymentType != "15") ? t("dialog.balanceAccountRequired") : ""}
                                />
                            </FormControl>
                        </Grid>
                  
           
                    <Grid item xs={12}>
                        <FormControl fullWidth margin="normal">
                            <PaymentProviderAutoComplete
                                key={viewModel.paymentProvider || null}
                                valueId={viewModel.paymentProvider || null}
                                onChange={(selected) => handleChange("paymentProvider", selected?.value || "")}
                                infoText={t("help.paymentProvider")}
                               
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth margin="normal">
                            <PaymentProviderConfigAutoComplete
                                key={viewModel.paymentProviderConfigId || null}
                                valueId={viewModel.paymentProviderConfigId || null}
                                onChange={(selected) => handleChange("paymentProviderConfigId", selected?.value || "")}
                                infoText={t("help.paymentProviderSetting")}
                                
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={!!viewModel.consumer} onChange={(e) => handleChange("consumer", e.target.checked)} />}
                            label={t("common.consumer")}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={!!viewModel.retail} onChange={(e) => handleChange("retail", e.target.checked)} />}
                            label={t("common.retail")}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={!!viewModel.global} onChange={(e) => handleChange("global", e.target.checked)} />}
                            label={t("common.global")}
                        />
                    </Grid>
                    {/*<Grid item xs={6}>*/}
                    {/*    <FormControlLabel*/}
                    {/*        control={<Switch checked={!!viewModel.refundFullHandling} onChange={(e) => handleChange("refundFullHandling", e.target.checked)} />}*/}
                    {/*        label={t("common.refundFullHandling")}*/}
                    {/*    />*/}
                    {/*</Grid>*/}
                    <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={!!viewModel.mposManualCard} onChange={(e) => handleChange("mposManualCard", e.target.checked)} />}
                            label={t("common.mposManualCard")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={showExtRefs} onChange={(e) => setShowExtRef(e.target.checked)} />}
                            label={t("common.showExternalReferences")}
                        />
                    </Grid>

                    {showExtRefs && (
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ padding: 2 }}>
           
                            <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Button variant="contained" onClick={() => setEditingLine({ ref: "", tempId: crypto.randomUUID() })} color="success">
                                        {t("common.create") + " " + t("common.externalReference")}
                                    </Button>
                                </Grid>
                                {viewModel.externalReferences?.map((item) => (
                                    <Grid item xs={12} key={item.id}>
                                        <Card
                                            onClick={() => handleSelectRef(item)}
                                            sx={{
                                                cursor: "pointer",
                                                transition: "box-shadow 0.2s ease-in-out",
                                                "&:hover": {
                                                    boxShadow: 4,
                                                    backgroundColor: "secondary.main",
                                                },
                                            }}
                                        >
                                 

                                            <CardContent>
                                                <Grid container alignItems="center">
                                                    <Grid item xs={10}>
                                                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                            {item.ref}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Id: {item.id}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={2} container justifyContent="flex-end">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveLine(item);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                            </Paper>
                        </Grid>
                            
                        
                    )}
                </Grid>
            )}

            <Dialog open={!!editingLine} onClose={() => setEditingLine(null)}>
                <DialogContent>
   
                    <Typography variant="h6">{t("common.editLine")}</Typography>

                    {editingLine && (
                        <>
                            <Grid container spacing={2}>

                                <Grid item xs={12} lg={12}>
                                    <Box sx={{ backgroundColor: "secondary.main" }}>
                                        <TextField
                                            label={t("common.externalReference")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.ref || ""}
                                            onChange={(e) => handleFieldChange("ref", e.target.value)}
                                        />
                                    </Box>
                                </Grid>
                                
                            </Grid>
                        </>

                    )}
                    <DialogActions>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Button
                                    onClick={handleSaveLine}
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    fullWidth
                                    disabled={!editingLine}
                                >
                                    {t("common.save")}
                                </Button>
                            </Grid>
                      
                            <Grid item xs={6}>
                                <Button
                                    onClick={() => setEditingLine(null)}
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    fullWidth
                                >
                                    {t("common.cancel")}
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    onClick={() => handleRemoveLine(editingLine)}
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    fullWidth
                                >
                                    {t("common.delete")}
                                </Button>
                            </Grid>
                     
                        </Grid>
                    </DialogActions>
                   
                </DialogContent>
            </Dialog>
            <Box sx={{ marginTop: 2, textAlign: "center" }}>
                <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
                    {t("common.save")}
                </Button>
                <Button variant="outlined" onClick={goBack}>
                    {t("common.back")}
                </Button>
            </Box>
        </CenteredCard>
    );
};

export default PaymentMethodEditPage;
